import React, { forwardRef, useCallback, useEffect, useRef } from 'react';
import clsx from 'clsx';

import Icon from 'components/Icon';
import { SpriteIcon } from 'types/icons';

import css from './TextField.module.scss';

type InputProps = React.JSX.IntrinsicElements['input'];

interface Props extends InputProps {
    theme?: 'default' | 'bordered';
    error?: boolean;
    iconName?: SpriteIcon;
}

const TextField = forwardRef<HTMLInputElement, Props>(({ theme, error, iconName, ...rest }, ref) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const combinedRef = (ref as React.RefObject<HTMLInputElement>) || inputRef;

    // Prevent keyboard-shortcut listeners in parent components from triggering while typing is in progress
    const handleKeyDown = useCallback((event: KeyboardEvent) => {
        if (event.key !== 'Enter' && event.key !== 'Escape') {
            event.stopPropagation();
        }
    }, []);

    useEffect(() => {
        const inputElement = combinedRef.current;
        if (inputElement) {
            inputElement.addEventListener('keydown', handleKeyDown);
        }

        return () => {
            if (inputElement) {
                inputElement.removeEventListener('keydown', handleKeyDown);
            }
        };
    }, [handleKeyDown, combinedRef]);

    return (
        <div className={clsx(css.textFieldWrapper, iconName && css.withIcon)}>
            {iconName && <Icon name={iconName} width={16} className={css.icon} color="gray3" />}
            <input
                ref={combinedRef}
                className={clsx(css.textField, theme && css.theme, error && css.error)}
                {...rest}
            />
        </div>
    );
});

TextField.displayName = 'TextField';

export default React.memo(TextField);
